/**
 * src/types/core/defaults.shared.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Shared default config blocks and TextElement builder helpers.
 * Every chart module imports from here to build its DEFAULTS object.
 *
 * Centralising defaults here prevents drift — if the global default legend
 * style changes, every chart picks it up automatically.
 *
 * Chart2Video | CORSphere Software Inc.
 */

import type { TextElement, BackgroundConfig, ChartLayoutConfig, AxesConfig, LegendConfig, DataLabelConfig } from './interfaces.shared';
import { TEXT_STYLE_DEFAULTS } from './interfaces.shared';


// =============================================================================
// TEXT ELEMENT BUILDERS
// =============================================================================

export const makeTitle = (text: string, id = 'title-1'): TextElement => ({
  id,
  text,
  style:    { ...TEXT_STYLE_DEFAULTS.title },
  position: { x: 0, y: 0 },
  visible:  true,
  animate:  true,
});

export const makeSubtitle = (text: string, id = 'subtitle-1'): TextElement => ({
  id,
  text,
  style:    { ...TEXT_STYLE_DEFAULTS.subtitle },
  position: { x: 0, y: 0 },
  visible:  true,
  animate:  true,
});

export const makeInfoBox = (text: string, id = 'infobox-1'): TextElement => ({
  id,
  text,
  style:    { ...TEXT_STYLE_DEFAULTS.infoBox },
  position: { x: 0, y: 0 },
  visible:  true,
  animate:  true,
});


// =============================================================================
// SHARED DEFAULT BLOCKS
// =============================================================================

export const DEFAULT_BACKGROUND: BackgroundConfig = {
  type:     'color',
  color:    '#FFFFFF',
  image:    null,
  opacity:  1,
  contrast: 1,
};

export const DEFAULT_LAYOUT: ChartLayoutConfig = {
  chartPosition: { x: 0, y: 0 },
  chartMargin:   { top: 180, right: 100, bottom: 150, left: 150 },
};

export const DEFAULT_AXIS = {
  visible:       true,
  labelStyle:    { ...TEXT_STYLE_DEFAULTS.axisLabel },
  labelDensity:  'auto'  as const,
  labelRotation: 0,
  labelSpacing:  30,
  showGridLines: false,
  gridStyle:     'light' as const,
  lineColor:     'rgba(148,163,184,0.6)',
  lineWidth:     1.5,
  tickLength:    4,
  tickStyle:     'solid' as const,
};

export const DEFAULT_Y_AXIS = {
  ...DEFAULT_AXIS,
  showGridLines: true,
  minValue:      'zero' as const,
  maxValue:      'auto' as const,
};

export const DEFAULT_AXES: AxesConfig = {
  x: { ...DEFAULT_AXIS   },
  y: { ...DEFAULT_Y_AXIS },
};

export const DEFAULT_LEGEND: LegendConfig = {
  visible:  true,
  style:    { ...TEXT_STYLE_DEFAULTS.legend },
  position: 'inline',
};

export const DEFAULT_DATA_LABELS: DataLabelConfig = {
  visible: true,
  style:   { ...TEXT_STYLE_DEFAULTS.dataLabel },
  format:  'value',
};

export const DEFAULT_PHYSICS = {
  bounce:        0.35,   // subtle natural spring — maps to damping=0.685
  springFreq:    2.5,
  staggerSpread: 0.45,
} as const;
